import { useState } from 'react'
import { useTicket } from '@/queries/tickets'
import { MessageThread } from './MessageThread'
import { ContextPanel } from './ContextPanel'
import { ReplyComposer } from './ReplyComposer'
import { CloseTicketButton } from './CloseTicketButton'
import { FullPageSpinner } from '@/components/etainabl-spinner'
import { ViewingAsBanner } from '@/features/view-as/ViewingAsBanner'
import { InvestigationRailCard } from '@/features/ai-investigate/InvestigationRailCard'
import { isOwnerViewer } from '@/lib/scope'
import { formatTicketRef } from '@/lib/ticketRef'
import type { ReactNode } from 'react'

export function TicketDetailView({ ticketId, adminControls, asUser, showInvestigations }: {
  ticketId: string
  adminControls?: ReactNode
  asUser?: string
  showInvestigations?: boolean
}) {
  const { data, isLoading, error } = useTicket(ticketId, asUser)
  // Remounting the composer (via `key`) is how a fresh seed overwrites
  // whatever the user was already typing, instead of colliding with it.
  const [draftSeed, setDraftSeed] = useState<{ text: string; v: number } | null>(null)
  if (isLoading) return <FullPageSpinner />

  // A forged asUser on a non-admin session gets an explicit message, not a
  // blank "not found" that reads as a broken link.
  const status = (error as { status?: number; statusCode?: number } | null)?.status
    ?? (error as { statusCode?: number } | null)?.statusCode
  if (status === 403) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        You don&apos;t have access to view as another user.
      </div>
    )
  }

  if (error || !data) return <div className="p-8">Ticket not found.</div>

  // The rail is the staff workbench: property controls, the AI investigation,
  // the page context and the action trail. Only the admin route passes
  // adminControls, so its presence IS staff-ness — a customer's own ticket page
  // is the conversation and nothing else (approved mockup, "Conversation +
  // slim rail" / client view). Everything inside the rail also self-gates
  // (TrackerControls and ActionTrail both re-check isAdmin).
  const staffView = Boolean(adminControls)

  // Closing is the reporter's own call to make; a colleague or company admin
  // reading the same ticket doesn't get it, and neither does the read-only
  // view-as lens. A tracked issue is excluded because its status is derived
  // from the tracker — the API refuses that close for the same reason.
  const canClose =
    !data.viewingAs &&
    isOwnerViewer(data.viewerRelation) &&
    data.ticket.status !== 'closed' &&
    !data.ticket.tracker

  const conversation = (
    <>
      <header className="flex shrink-0 items-start gap-3.5 px-6 pb-1 pt-6">
        <span className="shrink-0 pt-1 text-etn-control font-semibold tabular-nums text-etn-text-soft">
          {formatTicketRef(data.ticket)}
        </span>
        <h1 className="min-w-0 flex-1 truncate text-[1.25rem] font-semibold leading-tight">{data.ticket.subject}</h1>
      </header>
      <div className="min-h-0 flex-1 overflow-y-auto px-6">
        <MessageThread ticketId={ticketId} messages={data.messages} />
      </div>
      <div className="shrink-0 border-t bg-background">
        {data.viewingAs ? (
          <div className="p-4 text-sm text-muted-foreground">
            This is a read-only view — replies are disabled while viewing as another user.
          </div>
        ) : (
          <>
            {canClose && (
              <div className="flex justify-end px-4 pt-3">
                <CloseTicketButton ticketId={ticketId} />
              </div>
            )}
            <ReplyComposer
              key={draftSeed?.v ?? 0}
              ticketId={ticketId}
              status={data.ticket.status}
              viewer={data.viewerRelation}
              initialBody={draftSeed?.text}
            />
          </>
        )}
      </div>
    </>
  )

  return (
    // Negative margins cancel AppShell main's padding (px-8 pt-7 pb-12) so the
    // pane is full-bleed, and its height is the viewport minus the h-14 navbar,
    // so the composer stays on screen however long the thread gets. Both must
    // track AppShell/Navbar: a mismatch pushes the composer below the fold.
    <div className="-mx-8 -mt-7 -mb-12 flex h-[calc(100dvh-3.5rem)] flex-col">
      {data.viewingAs && <ViewingAsBanner name={data.viewingAs.name} companyName={data.viewingAs.companyName} />}
      <div className="flex min-h-0 flex-1">
        {/* One conversation, mounted once. Staff get it as the flexible left
            column with the rail beside it; a customer gets the same element
            centred at reading width. The two views differ in what is composed
            AROUND the conversation, never in the conversation itself. */}
        <div
          className={`flex min-w-0 flex-col overflow-hidden ${staffView ? 'flex-1' : 'mx-auto w-full max-w-[880px]'}`}
          data-testid="conversation"
        >
          {conversation}
        </div>
        {staffView && (
          <aside
            className="w-[292px] shrink-0 overflow-y-auto border-l border-etn-border-hairline bg-background px-5 pb-10 pt-5"
            data-testid="ticket-rail"
          >
            {adminControls}
            {showInvestigations && (
              <InvestigationRailCard
                ticketId={ticketId}
                onUseAsReply={text => setDraftSeed(s => ({ text, v: (s?.v ?? 0) + 1 }))}
              />
            )}
            <ContextPanel ticket={data.ticket} links={data.links} viewingAs={data.viewingAs} />
          </aside>
        )}
      </div>
    </div>
  )
}
